import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithPopup, User } from 'firebase/auth';
import { doc, getDoc, getDocs, setDoc, collection, collectionGroup } from 'firebase/firestore';
import { auth, googleProvider, db, promoteToSuperAdmin } from './firebase';
import { Receipt, ShieldCheck, ShieldOff, Users, CalendarDays, TrendingUp, Eye, Settings, Save } from 'lucide-react';
import './SuperAdminSetup.css';

const SUPER_ADMIN_EMAIL = 'mohapatra.shyam@gmail.com';

interface EventDoc {
  id: string;
  name: string;
  eventCode: string;
  adminId: string;
  createdAt: number;
  eventDate: number;
  totalMembers: number;
  contributors: any[];
  venue?: string;
}

interface UserRole {
  uid: string;
  role: string;
  email?: string;
  displayName?: string;
  createdAt?: number;
}

interface SiteStats {
  adminPageViews?: number;
  memberPageViews?: number;
}

function fmt(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function thisMonth(ts: number) {
  const d = new Date(ts);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

export default function SuperAdminSetup() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Dashboard data
  const [events, setEvents] = useState<EventDoc[]>([]);
  const [users, setUsers] = useState<UserRole[]>([]);
  const [stats, setStats] = useState<SiteStats>({});
  const [dataLoading, setDataLoading] = useState(false);

  // Config
  const [maxScans, setMaxScans] = useState<number>(60);
  const [maxScansInput, setMaxScansInput] = useState<string>('60');
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const snap = await getDoc(doc(db, 'userRoles', u.uid));
        setCurrentRole(snap.exists() ? (snap.data().role || 'user') : 'user');
      }
      setLoading(false);
    });
  }, []);

  const alreadySuperAdmin = currentRole === 'super_admin';

  useEffect(() => {
    if (!alreadySuperAdmin && !done) return;
    setDataLoading(true);

    Promise.all([
      getDocs(collectionGroup(db, 'events')).then(snap =>
        snap.docs.map(d => ({ id: d.id, ...d.data() } as EventDoc))
          .sort((a, b) => b.createdAt - a.createdAt)
      ),
      getDocs(collection(db, 'userRoles')).then(snap =>
        snap.docs.map(d => ({ uid: d.id, ...d.data() } as UserRole))
      ),
      getDoc(doc(db, 'analytics', 'siteStats')).then(snap =>
        snap.exists() ? (snap.data() as SiteStats) : {}
      ),
      getDoc(doc(db, 'analytics', 'config')).then(snap =>
        snap.exists() ? (snap.data().maxScansPerEvent ?? 60) : 60
      ),
    ]).then(([evs, usrs, st, limit]) => {
      setEvents(evs);
      setUsers(usrs);
      setStats(st);
      setMaxScans(limit as number);
      setMaxScansInput(String(limit));
      setDataLoading(false);
    }).catch(() => setDataLoading(false));
  }, [alreadySuperAdmin, done]);

  async function saveConfig() {
    const val = parseInt(maxScansInput, 10);
    if (isNaN(val) || val < 1 || val > 1000) return;
    setSavingConfig(true);
    try {
      await setDoc(doc(db, 'analytics', 'config'), { maxScansPerEvent: val }, { merge: true });
      setMaxScans(val);
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2500);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingConfig(false);
    }
  }

  async function claimSuperAdmin() {
    if (!user) return;
    setSaving(true);
    try {
      await promoteToSuperAdmin(user.uid, user.email!, user.displayName);
      setCurrentRole('super_admin');
      setDone(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="sa-page">
        <div className="sa-center"><div className="sa-spinner" /></div>
      </div>
    );
  }

  const isAllowed = user?.email === SUPER_ADMIN_EMAIL;

  // ── Full dashboard for super admin ──────────────────────────────────────────
  if ((alreadySuperAdmin || done) && isAllowed) {
    const totalSubmissions = events.reduce((s, e) => s + (e.contributors?.length ?? 0), 0);
    const eventsThisMonth = events.filter(e => thisMonth(e.createdAt)).length;

    // Map adminId → email for display in events table
    const uidToEmail: Record<string, string> = {};
    users.forEach(u => { if (u.uid && u.email) uidToEmail[u.uid] = u.email; });

    // Events per user
    const eventsPerUser: Record<string, number> = {};
    events.forEach(e => {
      eventsPerUser[e.adminId] = (eventsPerUser[e.adminId] ?? 0) + 1;
    });

    return (
      <div className="sa-page">
        <header className="sa-header">
          <button className="sa-back" onClick={() => navigate('/')}>← Dashboard</button>
          <div className="sa-logo">
            <div className="sa-logo-icon">
              <Receipt size={18} color="white" strokeWidth={2} />
            </div>
            <span className="sa-logo-text">XpenseMate</span>
          </div>
          <span className="sa-role-chip">super_admin</span>
        </header>

        <main className="sa-dash">
          <div className="sa-dash-head">
            <ShieldCheck size={28} className="sa-icon-ok" />
            <div>
              <h1 className="sa-dash-title">Admin Dashboard</h1>
              <p className="sa-dash-sub">{user?.email}</p>
            </div>
          </div>

          {/* ── Stats row ── */}
          <div className="sa-stats">
            <div className="sa-stat-card">
              <CalendarDays size={20} className="sa-stat-icon teal" />
              <div className="sa-stat-val">{dataLoading ? '—' : events.length}</div>
              <div className="sa-stat-lbl">Total events</div>
            </div>
            <div className="sa-stat-card">
              <Users size={20} className="sa-stat-icon blue" />
              <div className="sa-stat-val">{dataLoading ? '—' : users.length}</div>
              <div className="sa-stat-lbl">Admin users</div>
            </div>
            <div className="sa-stat-card">
              <TrendingUp size={20} className="sa-stat-icon green" />
              <div className="sa-stat-val">{dataLoading ? '—' : totalSubmissions}</div>
              <div className="sa-stat-lbl">Total submissions</div>
            </div>
            <div className="sa-stat-card">
              <Eye size={20} className="sa-stat-icon orange" />
              <div className="sa-stat-val">{dataLoading ? '—' : (stats.adminPageViews ?? 0)}</div>
              <div className="sa-stat-lbl">Admin page views</div>
            </div>
            <div className="sa-stat-card">
              <Eye size={20} className="sa-stat-icon pink" />
              <div className="sa-stat-val">{dataLoading ? '—' : (stats.memberPageViews ?? 0)}</div>
              <div className="sa-stat-lbl">Member page views</div>
            </div>
            <div className="sa-stat-card">
              <CalendarDays size={20} className="sa-stat-icon yellow" />
              <div className="sa-stat-val">{dataLoading ? '—' : eventsThisMonth}</div>
              <div className="sa-stat-lbl">Events this month</div>
            </div>
          </div>

          {/* ── Users table ── */}
          <section className="sa-section">
            <h2 className="sa-section-title">Admin users <span className="sa-count">{users.length}</span></h2>
            {dataLoading ? (
              <div className="sa-table-loading"><div className="sa-spinner" /></div>
            ) : users.length === 0 ? (
              <p className="sa-empty">No users found.</p>
            ) : (
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Events</th>
                      <th>UID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.uid}>
                        <td className="sa-td-email">{u.email ?? '—'}</td>
                        <td>{u.displayName ?? '—'}</td>
                        <td>
                          <span className={`sa-role-tag ${u.role === 'super_admin' ? 'super' : 'user'}`}>
                            {u.role ?? 'user'}
                          </span>
                        </td>
                        <td className="sa-td-num">{eventsPerUser[u.uid] ?? 0}</td>
                        <td className="sa-td-uid">{u.uid.slice(0, 10)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Settings ── */}
          <section className="sa-section">
            <h2 className="sa-section-title"><Settings size={16} /> App Settings</h2>
            <div className="sa-config-card">
              <div className="sa-config-row">
                <div>
                  <div className="sa-config-label">Max receipt scans per event</div>
                  <div className="sa-config-hint">
                    How many times Vision AI can be called per event. Current: <strong>{maxScans}</strong>
                  </div>
                </div>
                <div className="sa-config-input-row">
                  <input
                    type="number"
                    className="sa-config-input"
                    value={maxScansInput}
                    min={1}
                    max={1000}
                    onChange={e => setMaxScansInput(e.target.value)}
                  />
                  <button
                    className={`sa-config-save ${configSaved ? 'saved' : ''}`}
                    onClick={saveConfig}
                    disabled={savingConfig || parseInt(maxScansInput, 10) === maxScans}
                  >
                    {configSaved ? '✓ Saved' : savingConfig ? 'Saving…' : <><Save size={14} /> Save</>}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ── Events table ── */}
          <section className="sa-section">
            <h2 className="sa-section-title">All events <span className="sa-count">{events.length}</span></h2>
            {dataLoading ? (
              <div className="sa-table-loading"><div className="sa-spinner" /></div>
            ) : events.length === 0 ? (
              <p className="sa-empty">No events found.</p>
            ) : (
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Code</th>
                      <th>Creator</th>
                      <th>Created</th>
                      <th>Members</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(e => (
                      <tr key={e.id}>
                        <td className="sa-td-name">
                          <a
                            href={`/admin/event/${e.eventCode}`}
                            className="sa-event-link"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {e.name}
                          </a>
                          {e.venue ? <span className="sa-venue">{e.venue}</span> : null}
                        </td>
                        <td className="sa-td-code">{e.eventCode}</td>
                        <td className="sa-td-email">{uidToEmail[e.adminId] ?? e.adminId.slice(0, 8) + '…'}</td>
                        <td className="sa-td-date">{fmt(e.createdAt)}</td>
                        <td className="sa-td-num">{e.totalMembers}</td>
                        <td className="sa-td-num">{e.contributors?.length ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    );
  }

  // ── Setup / access-denied screens ───────────────────────────────────────────
  return (
    <div className="sa-page">
      <header className="sa-header">
        <button className="sa-back" onClick={() => navigate('/')}>← Back</button>
        <div className="sa-logo">
          <div className="sa-logo-icon">
            <Receipt size={18} color="white" strokeWidth={2} />
          </div>
          <span className="sa-logo-text">XpenseMate</span>
        </div>
      </header>

      <main className="sa-main">
        {!user ? (
          <>
            <ShieldOff size={48} className="sa-icon-deny" />
            <h1 className="sa-title">Super Admin Setup</h1>
            <p className="sa-subtitle">You need to sign in first.</p>
            <button
              className="sa-btn-signin"
              onClick={() => signInWithPopup(auth, googleProvider)}
            >
              Sign in with Google
            </button>
          </>
        ) : !isAllowed ? (
          <>
            <ShieldOff size={48} className="sa-icon-deny" />
            <h1 className="sa-title">Access Denied</h1>
            <p className="sa-subtitle">
              <strong>{user.email}</strong> is not authorized to claim super admin.
            </p>
            <button className="sa-btn-back" onClick={() => navigate('/')}>← Go back</button>
          </>
        ) : (
          <>
            <ShieldCheck size={48} className="sa-icon-pending" />
            <h1 className="sa-title">Super Admin Setup</h1>
            <p className="sa-subtitle">Signed in as <strong>{user.email}</strong></p>
            <div className="sa-info-row">
              <span className="sa-info-label">Current role</span>
              <span className="sa-info-val">{currentRole}</span>
            </div>
            <button
              className="sa-btn-claim"
              onClick={claimSuperAdmin}
              disabled={saving}
            >
              {saving ? 'Setting up…' : 'Claim Super Admin Role'}
            </button>
          </>
        )}
      </main>
    </div>
  );
}
